import React from 'react';
import { RUACH_FORCES } from '../ruach';

const SCIENCE_FORCES = [
  { name: 'Electromagnetism', carrier: 'Photon (γ)', range: 'Infinite', higgs: 'none', color: '#ffffff', detail: 'Massless photon = infinite range. The photon is protected by U(1) gauge symmetry which survives electroweak symmetry breaking.', acts: 'All charged particles' },
  { name: 'Weak nuclear', carrier: 'W±, Z⁰', range: '~10⁻¹⁸ m', higgs: 'full', color: '#ff9040', detail: 'The Higgs mechanism gives W and Z bosons enormous mass, limiting the weak force to distances far smaller than a proton. This governs radioactive decay and stellar fusion.', acts: 'All fermions' },
  { name: 'Strong nuclear (QCD)', carrier: 'Gluon (g)', range: '~10⁻¹⁵ m', higgs: 'none', color: '#e060ff', detail: 'Gluons are massless but the strong force is confined by color confinement. Confinement energy creates ~99% of proton/neutron mass.', acts: 'Quarks (colour charge)' },
  { name: 'Gravity', carrier: 'Graviton? (unconfirmed)', range: 'Infinite', higgs: 'outside', color: 'rgba(255,255,255,0.3)', detail: 'Gravity remains outside the Standard Model. No quantum theory of gravity yet exists.', acts: 'All energy/mass' },
];

export default function ForcesSummary({ mode }) {
  const [expanded, setExpanded] = React.useState(null);
  const isRuach = mode === 'ruach';
  const forces = isRuach ? RUACH_FORCES : SCIENCE_FORCES;
  const accentColor = isRuach ? '#ffb060' : '#f0c040';

  return (
    <div>
      <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.75rem', letterSpacing: '0.1em', marginBottom: '1.5rem', fontFamily: 'Space Mono', textTransform: 'uppercase' }}>
        {isRuach ? 'The four pillars of creation' : 'Four forces — Higgs role in each'}
      </p>
      <div style={{ padding: '0.875rem 1rem', marginBottom: '1.25rem', borderRadius: '8px', background: `${accentColor}0d`, border: `1px solid ${accentColor}30` }}>
        <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)', margin: 0, lineHeight: 1.7 }}>
          {isRuach
            ? <><span style={{ color: accentColor, fontWeight: 600 }}>Ancient witness: </span>"Since the creation of the world God's invisible qualities have been clearly seen, being understood from what has been made." — Romans 1:20</>
            : <><span style={{ color: accentColor, fontWeight: 600 }}>Key insight: </span>Mass and force range are linked via the Higgs. A massless carrier → infinite range. A massive carrier → short range.</>}
        </p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
        {forces.map(f => (
          <div key={f.name} onClick={() => setExpanded(expanded === f.name ? null : f.name)} style={{
            borderRadius: '8px', overflow: 'hidden', cursor: 'pointer',
            border: `1px solid ${f.color}30`,
            background: expanded === f.name ? `${f.color}08` : 'rgba(255,255,255,0.03)',
            transition: 'background 0.2s',
          }}>
            <div style={{ padding: '0.875rem 1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: '0.88rem', fontWeight: 600, color: f.color, fontFamily: 'Syne', marginBottom: '0.2rem' }}>{f.name}</div>
                <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', fontFamily: 'Space Mono' }}>{f.carrier} · Range: {f.range}</div>
              </div>
              <div style={{
                fontSize: '0.62rem', padding: '3px 8px', borderRadius: '4px', fontFamily: 'Space Mono', whiteSpace: 'nowrap',
                background: (f.higgs || f.ruach) === 'full' ? `${accentColor}20` : (f.higgs || f.ruach) === 'outside' ? 'rgba(255,255,255,0.05)' : 'rgba(255,96,96,0.12)',
                color: (f.higgs || f.ruach) === 'full' ? accentColor : (f.higgs || f.ruach) === 'outside' ? 'rgba(255,255,255,0.3)' : '#ff9090',
              }}>
                {isRuach ? (f.ruach === 'full' ? 'Ruach ✓' : f.ruach === 'outside' ? 'beyond knowing' : 'no Ruach mass') : (f.higgs === 'full' ? 'Higgs ✓' : f.higgs === 'outside' ? 'outside SM' : 'no Higgs')}
              </div>
            </div>
            {expanded === f.name && (
              <div style={{ padding: '0 1rem 0.875rem', borderTop: `1px solid ${f.color}15` }}>
                <p style={{ fontSize: '0.82rem', lineHeight: 1.7, color: 'rgba(255,255,255,0.55)', margin: '0.75rem 0 0' }}>{f.detail}</p>
                {isRuach && f.scripture && (
                  <p style={{ fontSize: '0.75rem', color: 'rgba(255,160,60,0.6)', margin: '0.6rem 0 0', fontStyle: 'italic', lineHeight: 1.6 }}>{f.scripture}</p>
                )}
                <div style={{ marginTop: '0.5rem', fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)', fontFamily: 'Space Mono' }}>Acts on: {f.acts}</div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
